import React, { useState, useMemo, useEffect, useRef, Suspense } from 'react';
import axios from 'axios';
import FilterPanel from '../Filters/FilterPanel';
import ChatBot from '../ChatBot/ChatBot';
import { useProperty } from '../../context/PropertyContext';
import { useSiteContent } from '../../context/SiteContentContext';
import { useAuth } from '../../context/AuthContext';
import FeedbackModal from '../Support/FeedbackModal';
import { mediaUrl } from '../../lib/mediaUrl';
import { INFO_PAGES } from './infoPages';
import './FinderPage.css';

// The map library is big and the home page doesn't show a map, so it's
// downloaded only when someone opens Map view.
const MapView = React.lazy(() => import('../Map/MapView'));

function StarRow({ rating, size = 14 }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1,2,3,4,5].map(s => (
        <svg key={s} width={size} height={size} viewBox="0 0 24 24"
          fill={s <= rating ? '#f59e0b' : 'none'}
          stroke="#f59e0b" strokeWidth="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ))}
    </div>
  );
}

const AVATAR_COLORS = ['#e53935','#1976d2','#388e3c','#f57c00','#7b1fa2','#00838f','#c62828','#0277bd'];
const getAvatarColor = (name) => {
  if (!name || name === 'Anonymous' || name === 'Spaczo User') return '#9e9e9e';
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const formatReviewName = (name) => {
  if (!name || name === 'Anonymous') return 'Spaczo User';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

function CommunityReviews({ onWriteReview }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    axios.get('/api/support/feedback')
      .then(r => { setReviews(r.data.data || []); setFetchError(false); })
      .catch(() => { setReviews([]); setFetchError(true); })
      .finally(() => setLoading(false));
  }, []);

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  if (loading) return null;

  return (
    <div className="home-reviews-section">
      <div className="home-reviews-header">
        <div>
          <div className="home-section-title">What Our Users Say</div>
          {avg && (
            <div className="home-reviews-avg">
              <span className="home-reviews-score">{avg}</span>
              <StarRow rating={Math.round(avg)} size={16} />
              <span className="home-reviews-count">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        <button className="home-reviews-write-btn" onClick={onWriteReview}>
          ✍️ Write a Review
        </button>
      </div>

      {fetchError ? (
        <div className="home-reviews-empty">
          <p>Reviews temporarily unavailable. Please try again later.</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="home-reviews-empty">
          <p>No reviews yet — be the first!</p>
          <button className="btn btn-primary btn-sm" onClick={onWriteReview}>Share Your Experience</button>
        </div>
      ) : (
        <div className="home-reviews-scroll">
          {reviews.slice(0, 10).map((r, i) => {
            const displayName = formatReviewName(r.name);
            const avatarColor = getAvatarColor(r.name);
            const isVerified = r.is_verified || (r.name && r.name !== 'Anonymous' && r.name !== 'Spaczo User');
            return (
              <div key={r._id || i} className="home-review-card">
                <div className="hrc-top">
                  <div className="hrc-avatar" style={{ background: avatarColor }}>
                    {(r.name && r.name !== 'Anonymous' ? r.name[0] : 'S').toUpperCase()}
                  </div>
                  <div className="hrc-meta">
                    <div className="hrc-name">
                      <span className="hrc-name-text">{displayName}</span>
                      {isVerified && <span className="hrc-verified">✓ Verified</span>}
                    </div>
                    <div className="hrc-date">{new Date(r.created_at || r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                  </div>
                  <StarRow rating={r.rating} size={13} />
                </div>
                {r.category && (
                  <div className="hrc-cats">
                    {r.category.split(', ').map(c => (
                      <span key={c} className="hrc-cat">{c}</span>
                    ))}
                  </div>
                )}
                <p className="hrc-message">{r.message}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function FinderPage() {
  const { properties, loading, getNearbyProperties, applyFilters, filters, setUserLocation, setActiveTab, finderCategory, setFinderCategory, viewPickerCategory, setViewPickerCategory, userCity, noResultsIn, selectedProperty } = useProperty();
  const { hero } = useSiteContent();
  const { isFavorite, toggleFavorite } = useAuth();
  const [showNudge, setShowNudge] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showFindPicker, setShowFindPicker] = useState(false);
  const viewCountRef = useRef(0);
  const nudgeDismissedRef = useRef(false);


  /* "Finding what you need?" — only after real browsing (6 listings opened
     or 3 minutes), and once it has been shown it stays away for 30 days. */
  const NUDGE_KEY = 'spaczo_nudge_seen_at';
  const nudgeRecentlySeen = () => {
    try { return Date.now() - Number(localStorage.getItem(NUDGE_KEY) || 0) < 30 * 86400e3; } catch { return false; }
  };
  const openNudge = () => {
    if (nudgeDismissedRef.current || nudgeRecentlySeen()) return;
    nudgeDismissedRef.current = true;          // at most once per visit
    try { localStorage.setItem(NUDGE_KEY, String(Date.now())); } catch {}
    setShowNudge(true);
  };
  useEffect(() => {
    if (!finderCategory || nudgeDismissedRef.current || nudgeRecentlySeen()) return;
    const timer = setTimeout(openNudge, 180000);
    return () => clearTimeout(timer);
  }, [finderCategory]); // eslint-disable-line

  const trackPropertyView = () => {
    viewCountRef.current += 1;
    if (viewCountRef.current >= 6) openNudge();
  };

  const dismissNudge = () => {
    nudgeDismissedRef.current = true;
    setShowNudge(false);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(5);
  const [viewMode, setViewMode] = useState('list'); // 'map' | 'list'
  const [sortBy, setSortBy] = useState('newest'); // 'newest' | 'price_asc' | 'price_desc' | 'views'
  const [nearbySheet, setNearbySheet] = useState(false);
  const [nearbyLocating, setNearbyLocating] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [autoRadar, setAutoRadar] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  const [visibleCount, setVisibleCount] = useState(20);
  const [infoPage, setInfoPage] = useState(null);

  // Phones: the map is always full screen. Leaving it (Back, the list
  // button, the phone's back gesture) goes to the card view, never to a
  // squeezed half-height map.
  const isPhone = () => window.innerWidth <= 768;
  // Push a history entry when going fullscreen so back button exits fullscreen
  const enterFullscreen = () => {
    setMapFullscreen(true);
    window.__spaczoMapFullscreen = true;   // App's back handler leaves this one to us
    window.history.pushState({ mapFullscreen: true }, '');
  };
  const exitFullscreen = () => {
    setMapFullscreen(false);
    setAutoRadar(false);
    if (isPhone()) setViewMode('list');
    // Cleared a moment later so App's back handler, which may run after ours
    // for the same back press, still knows it was ours.
    setTimeout(() => { window.__spaczoMapFullscreen = false; }, 0);
  };
  const showMap = () => {
    setViewMode('map');
    if (isPhone() && !mapFullscreen) enterFullscreen();
  };
  const showList = () => {
    if (mapFullscreen) exitFullscreen();
    setViewMode('list');
  };

  // Back button exits fullscreen instead of leaving the app
  // Close find picker when clicking outside
  useEffect(() => {
    if (!showFindPicker) return;
    const close = (e) => { if (!e.target.closest('.hero-find-wrap')) setShowFindPicker(false); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showFindPicker]);

  useEffect(() => {
    const onPopState = (e) => {
      if (mapFullscreen) {
        exitFullscreen();
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [mapFullscreen]);

  // Results come first: the Filters panel only opens when the visitor taps
  // Filters (it used to open by itself in Map View and cover the results).
  const alertMe = () => {
    try { sessionStorage.setItem('spaczo_open_alert_form', '1'); } catch {}
    setActiveTab('alerts');
  };
  const noResultsBanner = noResultsIn && !loading && (
    <div className="no-results-banner" role="status">
      <span>No {finderCategory === 'commercial' ? 'spaces' : 'rooms'} listed in <strong>{noResultsIn}</strong> yet. Showing listings from other places.</span>
      {viewMode === 'map' && <button onClick={showList}>See them</button>}
      <button onClick={alertMe}>🔔 Alert me</button>
    </div>
  );

  const sortedProperties = useMemo(() => {
    let arr = finderCategory
      ? properties.filter(p => p.category === finderCategory)
      : [...properties];
    if (sortBy === 'price_asc') return arr.sort((a, b) => a.price - b.price);
    if (sortBy === 'price_desc') return arr.sort((a, b) => b.price - a.price);
    if (sortBy === 'views') return arr.sort((a, b) => (b.views || 0) - (a.views || 0));
    return arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [properties, sortBy, finderCategory]);

  // Reset pagination when the category changes (moved out of useMemo, which must stay pure)
  useEffect(() => {
    setVisibleCount(20);
  }, [finderCategory]);

  const handleCategorySelect = (cat) => {
    setViewPickerCategory(cat);
  };

  const handleViewPick = (cat, mode) => {
    setFinderCategory(cat);
    applyFilters({ category: cat }, { track: false });
    setViewMode(mode);
    setViewPickerCategory(null);
    if (mode === 'map') handleGPS();
  };

  const activeFilterCount = [
    filters.propertyType,
    filters.city,
    filters.area,
    filters.minPrice,
    filters.maxPrice,
    ...(filters.amenities || [])
  ].filter(Boolean).length;

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setSearchError('');
    setSearching(true);
    setAutoRadar(false); // reset so next "Find Near Me" click always re-triggers GPS

    // Pincode search — detect 6-digit Indian pincode
    if (/^\d{6}$/.test(q)) {
      applyFilters({ pincode: q, city: '', area: '' });
      // Also geocode pincode to move the map to that location
      try {
        const res = await axios.get(
          `https://nominatim.openstreetmap.org/search?postalcode=${q}&country=India&format=json&limit=1`,
          { headers: { 'Accept-Language': 'en' } }
        );
        if (res.data.length > 0) {
          const { lat, lon } = res.data[0];
          const coords = { lat: parseFloat(lat), lng: parseFloat(lon) };
          setMapCenter([coords.lat, coords.lng]);
          setMapZoom(14);
          setUserLocation(coords);
          setViewMode('map');
          if (window.innerWidth <= 768) enterFullscreen();
          // Re-fetch with both pincode + proximity
          applyFilters({ pincode: q, lat: coords.lat, lng: coords.lng, radius: 15, city: '', area: '' });
        }
      } catch { /* keep pincode-only filter if geocoding fails */ }
      setSearching(false);
      return;
    }

    try {
      // Geocode using Nominatim (free, no API key)
      const res = await axios.get(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      if (res.data.length > 0) {
        const { lat, lon } = res.data[0];
        const city = q.split(',')[0].trim();
        const coords = { lat: parseFloat(lat), lng: parseFloat(lon) };
        setMapCenter([coords.lat, coords.lng]);
        setMapZoom(14);
        setUserLocation(coords);
        setViewMode('map');
        if (window.innerWidth <= 768) enterFullscreen();
        applyFilters({ city, lat: coords.lat, lng: coords.lng, radius: 10, pincode: '' });
      } else {
        setSearchError('Location not found. Try a city name or 6-digit pincode.');
      }
    } catch {
      applyFilters({ city: q, pincode: '' });
    } finally {
      setSearching(false);
    }
  };

  const handleGPS = () => {
    // Full-screen only on mobile (≤768px); desktop stays in normal layout
    const isMobile = window.innerWidth <= 768;
    setViewMode('map');
    if (isMobile) enterFullscreen();
    // Clear stale pincode/city filters so GPS fetch isn't contaminated
    applyFilters({ pincode: '', city: '', area: '', lat: '', lng: '', radius: '' }, { track: false });
    // autoRadar flip (false→true) triggers MapView's effect which calls
    // getNearbyProperties() — handles GPS acquisition, setUserLocation, and fetch.
    // MapAutoZoom inside MapView then flies the map to the GPS position automatically.
    setAutoRadar(true);
  };

  if (infoPage) {
    const page = INFO_PAGES[infoPage];
    return (
      <div className="finder-page info-page-wrap">
        <div className="info-page">
          <button className="page-back-btn" onClick={() => setInfoPage(null)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Back
          </button>
          <h2 className="info-page-title">{page.title}</h2>
          <div className="info-page-body">{page.content}</div>
        </div>
      </div>
    );
  }

  // ── View picker: shown after category is selected ─────────────
  if (viewPickerCategory) {
    const isRes = viewPickerCategory === 'residential';
    return (
      <div className="finder-page view-picker-page">
        <div className="view-picker-wrap">
          <button className="page-back-btn view-picker-back" onClick={() => setViewPickerCategory(null)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Back
          </button>
          <div className="view-picker-header">
            {/* Category toggle switcher */}
            <div className="vpc-cat-toggle">
              <button
                className={`vpc-cat-btn${isRes ? ' active' : ''}`}
                onClick={() => setViewPickerCategory('residential')}
              >
                🏠 Residential
              </button>
              <button
                className={`vpc-cat-btn${!isRes ? ' active' : ''}`}
                onClick={() => setViewPickerCategory('commercial')}
              >
                🏢 Commercial
              </button>
            </div>
            <h2>How do you want to browse?</h2>
            <p>Choose your preferred way to find {isRes ? 'rooms, flats & PGs' : 'offices, shops & spaces'}</p>
          </div>
          <div className="view-picker-cards">

            {/* Map View */}
            <button className="view-picker-card" onClick={() => handleViewPick(viewPickerCategory, 'map')}>
              <div className="vpc-icon vpc-icon-map">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                  <line x1="8" y1="2" x2="8" y2="18"/>
                  <line x1="16" y1="6" x2="16" y2="22"/>
                </svg>
              </div>
              <div className="vpc-body">
                <div className="vpc-title">Map View</div>
                <div className="vpc-desc">See properties on a live map · Find what's near you · Explore by area</div>
                <div className="vpc-tags">
                  <span>Near me</span>
                  <span>Location-based</span>
                  <span>Visual explore</span>
                </div>
              </div>
              <svg className="vpc-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>

            {/* Card View */}
            <button className="view-picker-card" onClick={() => handleViewPick(viewPickerCategory, 'list')}>
              <div className="vpc-icon vpc-icon-list">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                </svg>
              </div>
              <div className="vpc-body">
                <div className="vpc-title">Card View</div>
                <div className="vpc-desc">Browse all listings as cards · Filter by price, area & type · Compare easily</div>
                <div className="vpc-tags">
                  <span>All listings</span>
                  <span>Filter & sort</span>
                  <span>Compare prices</span>
                </div>
              </div>
              <svg className="vpc-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>

          </div>
        </div>
      </div>
    );
  }

  if (!finderCategory) {
    return (
      <>
      <div className="finder-page finder-category-pick">
        <div className="finder-cat-wrap">
          <div className="finder-home-hero">
            <div className="hero-speed-badge">
              <span className="hero-speed-dot" />
              {hero.badge}
            </div>
            <h2>{hero.title}{hero.highlight && <><br/><span>{hero.highlight}</span></>}</h2>
            {hero.subtitle && <p>{hero.subtitle}</p>}

            {/* Find Now CTA */}
            <div className="hero-find-wrap">
              <button
                className="hero-find-btn"
                onClick={() => setShowFindPicker(p => !p)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                Find Now
                <svg className={`hero-find-chevron${showFindPicker ? ' open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              </button>

              {showFindPicker && (
                <div className="hero-find-dropdown">
                  <button className="hero-find-option" onClick={() => { setViewPickerCategory('residential'); setShowFindPicker(false); }}>
                    <span className="hero-find-option-icon">🏠</span>
                    <div className="hero-find-option-text">
                      <span className="hero-find-option-title">Residential</span>
                      <span className="hero-find-option-sub">Rooms · PG · Flats · Villas</span>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                  <div className="hero-find-divider"/>
                  <button className="hero-find-option" onClick={() => { setViewPickerCategory('commercial'); setShowFindPicker(false); }}>
                    <span className="hero-find-option-icon">🏢</span>
                    <div className="hero-find-option-text">
                      <span className="hero-find-option-title">Commercial</span>
                      <span className="hero-find-option-sub">Office · Shop · Warehouse</span>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
              )}
            </div>

            <div className="finder-hero-pills">
              <div className="hero-pill hero-pill-btn" onClick={() => setActiveTab('agreement')}>
                <div className="hero-pill-icon green">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                </div>
                <div className="hero-pill-text">
                  <span className="hero-pill-title">Free Agreement</span>
                  <span className="hero-pill-sub">PDF in 2 minutes, ₹0</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick-commerce style promise strip */}
          <div className="qc-promise-strip">
            <div className="qc-promise">
              <span className="qc-promise-num">₹0</span>
              <span className="qc-promise-text">Broker fee.<br/>Always.</span>
            </div>
            <div className="qc-divider"/>
            <div className="qc-promise">
              <span className="qc-promise-num">60s</span>
              <span className="qc-promise-text">To list<br/>your property.</span>
            </div>
            <div className="qc-divider"/>
            <div className="qc-promise">
              <span className="qc-promise-num">50+</span>
              <span className="qc-promise-text">Cities across<br/>India.</span>
            </div>
            <div className="qc-divider"/>
            <div className="qc-promise">
              <span className="qc-promise-num">Direct</span>
              <span className="qc-promise-text">Owner contact.<br/>No middleman.</span>
            </div>
          </div>

          <div className="finder-cat-label">What are you looking for?</div>
          <div className="finder-cat-cards">
            <button className="finder-cat-card residential" onClick={() => handleCategorySelect('residential')}>
              <div className="fcat-icon">🏠</div>
              <div className="fcat-body">
                <div className="fcat-title">Rooms & Flats</div>
                <div className="fcat-desc">Talk directly to the owner. Move in faster.</div>
                <div className="fcat-tags">
                  <span>Room / PG</span><span>1BHK / 2BHK</span><span>Villa</span>
                </div>
              </div>
              <svg className="fcat-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
            <button className="finder-cat-card commercial" onClick={() => handleCategorySelect('commercial')}>
              <div className="fcat-icon">🏢</div>
              <div className="fcat-body">
                <div className="fcat-title">Office & Shop</div>
                <div className="fcat-desc">Zero commission. Start your business faster.</div>
                <div className="fcat-tags">
                  <span>Office</span><span>Shop</span><span>Warehouse</span>
                </div>
              </div>
              <svg className="fcat-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>


          {/* Community Reviews */}
          <CommunityReviews onWriteReview={() => setShowFeedbackModal(true)} />

          {/* Footer */}
          <div className="home-footer">
            <div className="home-footer-brand">
              <span className="home-footer-logo">Spaczo</span>
              <span className="home-footer-tagline">Zero brokerage · Direct owner contact · India</span>
              <a className="home-footer-ig" href="https://www.instagram.com/spaczo.india" target="_blank" rel="noopener noreferrer" aria-label="Spaczo on Instagram">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                <span>@spaczo.india</span>
              </a>
            </div>
            <div className="home-footer-links">
              <button onClick={() => setInfoPage('about')}>About Us</button>
              <button onClick={() => setInfoPage('contact')}>Contact</button>
              <button onClick={() => setInfoPage('pricing')}>Pricing</button>
              <button onClick={() => setInfoPage('refund')}>Refund Policy</button>
              <button onClick={() => setInfoPage('privacy')}>Privacy Policy</button>
              <button onClick={() => setInfoPage('terms')}>Terms of Service</button>
              <button onClick={() => setActiveTab('support')}>Support</button>
              <button onClick={() => setActiveTab('agreement')}>Free Agreement</button>
              <button onClick={() => setActiveTab('receipt')}>Rent Receipts</button>
            </div>
            <div className="home-footer-copy">© {new Date().getFullYear()} Spaczo. All rights reserved.</div>
          </div>

        </div>
      </div>

      {/* Floating Help button — opens AI assistant */}
      {!showChat && !selectedProperty && (
        <button
          className={`finder-help-fab ${unreadChat > 0 ? 'has-unread' : ''}`}
          onClick={() => { setShowChat(true); setUnreadChat(0); }}
          aria-label="Need help? Ask our assistant"
          title="Need help? Ask our assistant"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span>Help</span>
          {unreadChat > 0 && <span className="finder-chat-badge">{unreadChat}</span>}
        </button>
      )}

      <ChatBot
        open={showChat}
        onClose={() => setShowChat(false)}
        onUnread={() => setUnreadChat(n => n + 1)}
      />

      {showFeedbackModal && (
        <FeedbackModal triggerSource="finder" onClose={() => setShowFeedbackModal(false)} />
      )}
      </>
    );
  }

  return (
    <div className="finder-page">
      {/* Back + Category badge */}
      <div className="finder-cat-bar">
        <span className={`finder-cat-badge ${finderCategory}`}>
          {finderCategory === 'residential' ? '🏠 Residential' : '🏢 Commercial'}
        </span>
        <button className="finder-cat-change" onClick={() => {
          setFinderCategory(null);
          setSearchQuery('');
          applyFilters({ category: '', propertyType: '', minPrice: '', maxPrice: '', amenities: [], city: userCity || '', area: '', lat: '', lng: '', radius: '', pincode: '' }, { track: false });
        }}>
          Change Category
        </button>
        <button
          className={`finder-chat-btn ${unreadChat > 0 ? 'has-unread' : ''}`}
          onClick={() => { setShowChat(v => !v); setUnreadChat(0); }}
          title="Ask our assistant"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          Help
          {unreadChat > 0 && <span className="finder-chat-badge">{unreadChat}</span>}
        </button>
      </div>

      {/* Top search bar */}
      <div className="finder-toolbar">
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-input-wrap">
            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="City, area, or 6-digit pincode..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" className="search-clear" onClick={() => {
                setSearchQuery('');
                setSearchError('');
                setAutoRadar(false);
                applyFilters({ city: userCity || '', area: '', pincode: '', lat: '', lng: '', radius: '' }, { track: false });
              }} aria-label="Clear search">✕</button>
            )}
          </div>
          <button type="submit" className="btn btn-primary" disabled={searching}>
            {searching ? <span className="spinner" style={{ width: 16, height: 16 }} /> : 'Search'}
          </button>
        </form>
        {searchError && (
          <div className="auth-error" style={{ margin: '6px 0 0', fontSize: 13 }}>{searchError}</div>
        )}

        {/* Location context chip */}
        {(filters.city || userCity) && !searchQuery && !noResultsIn && (
          <div className="search-location-chip">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            Showing in <strong>{filters.city || userCity}</strong>
            {userCity && filters.city !== userCity && (
              <button className="slc-back" onClick={() => {
                setSearchQuery('');
                applyFilters({ city: userCity, area: '', pincode: '', lat: '', lng: '', radius: '' }, { track: false });
              }}>
                ← Back to {userCity}
              </button>
            )}
            {!userCity && filters.city && (
              <button className="slc-clear" onClick={() => {
                setSearchQuery('');
                applyFilters({ city: '', area: '', pincode: '', lat: '', lng: '', radius: '' }, { track: false });
              }}>
                See all
              </button>
            )}
          </div>
        )}

        <button className="btn btn-secondary gps-btn" onClick={handleGPS}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
          </svg>
          {finderCategory === 'commercial' ? 'Find Space Near Me' : 'Find Room Near Me'}
        </button>

        <button
          className={`btn btn-secondary filter-btn ${activeFilterCount > 0 ? 'has-filters' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          Filters
          {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
        </button>

        <select className="sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="newest">Newest First</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
          <option value="views">Most Viewed</option>
        </select>

        <div className="view-toggle">
          <button
            className={`view-btn ${viewMode === 'map' ? 'active' : ''}`}
            onClick={showMap}
            title="Map View"
            aria-label="Map view"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
              <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
            </svg>
          </button>
          <button
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={showList}
            title="List View"
            aria-label="List view"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      {!mapFullscreen && noResultsBanner}

      <div className="finder-body">
        {/* Filter Panel — list view sidebar (desktop) */}
        {showFilters && viewMode !== 'map' && (
          <div className="filter-sidebar">
            <FilterPanel onClose={() => setShowFilters(false)} />
          </div>
        )}

        {/* Map or List */}
        {viewMode === 'map' ? (
          <div className={`map-container${mapFullscreen ? ' map-fullscreen' : ''}`}>
            {/* Filter Panel overlay — inside map container so it always appears on top */}
            {showFilters && (
              <div className="map-filter-overlay">
                <FilterPanel onClose={() => setShowFilters(false)} />
              </div>
            )}
            {mapFullscreen && noResultsBanner && <div className="no-results-banner-float">{noResultsBanner}</div>}
            {mapFullscreen && (
              <button className="map-back-btn" onClick={exitFullscreen}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                Back
              </button>
            )}

            {/* Mobile-only floating toolbar on map */}
            <div className="map-mobile-toolbar">
              <button
                className={`mmt-btn ${activeFilterCount > 0 ? 'has-filters' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
                Filters
                {activeFilterCount > 0 && <span className="mmt-badge">{activeFilterCount}</span>}
              </button>

              <select className="mmt-sort" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="newest">Newest</option>
                <option value="price_asc">Price ↑</option>
                <option value="price_desc">Price ↓</option>
                <option value="views">Popular</option>
              </select>

              <div className="mmt-toggle">
                <button className="mmt-view-btn active" title="Map View" aria-label="Map view">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                    <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
                  </svg>
                </button>
                <button className="mmt-view-btn" title="List View" aria-label="List view" onClick={showList}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/>
                    <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/>
                    <line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>

            {loading && !mapFullscreen && (
              <div className="map-loading">
                <div className="spinner" />
                <span>Finding properties...</span>
              </div>
            )}
            <Suspense fallback={<div className="map-loading"><div className="spinner" /><span>Loading map...</span></div>}>
              <MapView mapCenter={mapCenter} mapZoom={mapZoom} autoRadar={autoRadar} category={finderCategory} />
            </Suspense>
          </div>
        ) : (
          <div className="list-container">
            {loading ? (
              <div className="property-list">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="prop-card prop-card-skeleton">
                    <div className="skeleton-img" />
                    <div className="skeleton-body">
                      <div className="skeleton-line w60" />
                      <div className="skeleton-line w90" />
                      <div className="skeleton-line w45" />
                      <div className="skeleton-tags">
                        <div className="skeleton-tag" /><div className="skeleton-tag" /><div className="skeleton-tag" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : properties.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🏠</div>
                <h3>No properties found</h3>
                <p>Try adjusting filters or searching a different area</p>
              </div>
            ) : (
              <div className="property-list">
                {sortedProperties.slice(0, visibleCount).map((p, i) => <PropertyCard key={p._id} property={p} eager={i < 2} isFav={isFavorite(p._id)} onToggleFav={() => toggleFavorite(p._id)} onView={trackPropertyView} />)}
                {visibleCount < sortedProperties.length && (
                  <button className="load-more-btn" onClick={() => setVisibleCount(c => c + 20)}>
                    Load more ({sortedProperties.length - visibleCount} remaining)
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Finder feedback nudge */}
      {showNudge && !showFeedbackModal && (
        <FinderNudge
          onDismiss={dismissNudge}
          onFeedback={() => { dismissNudge(); setShowFeedbackModal(true); }}
        />
      )}
      {showFeedbackModal && (
        <FeedbackModal triggerSource="finder" onClose={() => setShowFeedbackModal(false)} />
      )}

      <ChatBot
        open={showChat}
        onClose={() => setShowChat(false)}
        onUnread={() => setUnreadChat(n => n + 1)}
      />

      {/* ── Nearby Sheet ── */}
      {nearbySheet && (
        <NearbySheet
          properties={properties}
          userCoords={userCoords}
          loading={loading}
          isFavorite={isFavorite}
          toggleFavorite={toggleFavorite}
          onViewMap={() => { setNearbySheet(false); showMap(); }}
          onClose={() => setNearbySheet(false)}
        />
      )}
    </div>
  );
}

/* ── Distance helper ── */
function getDist(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/* ── Nearby Sheet ── */
function NearbySheet({ properties, userCoords, loading, isFavorite, toggleFavorite, onViewMap, onClose }) {
  const { setSelectedProperty } = useProperty();

  const nearby = useMemo(() => {
    if (!userCoords) return properties.slice(0, 20);
    return properties
      .map(p => {
        const c = p.location?.coordinates;
        if (!c?.lat || !c?.lng) return null;
        const dist = getDist(userCoords.lat, userCoords.lng, c.lat, c.lng);
        return { ...p, dist };
      })
      .filter(Boolean)
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 30);
  }, [properties, userCoords]);

  return (
    <div className="nearby-sheet-overlay" onClick={onClose}>
      <div className="nearby-sheet" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="nbs-header">
          <div className="nbs-handle-bar" />
          <div className="nbs-title-row">
            <div className="nbs-title-left">
              <div className="nbs-icon">📍</div>
              <div>
                <div className="nbs-title">Rooms Near You</div>
                <div className="nbs-sub">
                  {loading ? 'Finding properties…' : `${nearby.length} properties found`}
                  {userCoords && !loading && ' · within 10km'}
                </div>
              </div>
            </div>
            <div className="nbs-header-actions">
              <button className="nbs-map-btn" onClick={onViewMap}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                  <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
                </svg>
                Map
              </button>
              <button className="nbs-close" onClick={onClose}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="nbs-body">
          {loading ? (
            <div className="nbs-loading">
              <div className="spinner" />
              <span>Finding rooms near you…</span>
            </div>
          ) : nearby.length === 0 ? (
            <div className="nbs-empty">
              <div className="nbs-empty-icon">🏠</div>
              <div className="nbs-empty-title">No properties nearby</div>
              <div className="nbs-empty-sub">Try expanding your search area</div>
            </div>
          ) : (
            <div className="nbs-list">
              {nearby.map((p, i) => (
                <div key={p._id} className="nbs-item" onClick={e => { e.stopPropagation(); setSelectedProperty(p); onClose(); }}>
                  <div className="nbs-item-img">
                    {p.media?.images?.[0]
                      ? <img src={mediaUrl(p.media.images[0])} alt={p.title} loading="lazy" />
                      : <div className="nbs-item-placeholder">🏠</div>
                    }
                    <span className="nbs-item-rank">#{i + 1}</span>
                    {p.dist !== undefined && (
                      <span className="nbs-item-dist">
                        {p.dist < 1 ? (p.dist * 1000).toFixed(0) + 'm' : p.dist.toFixed(1) + 'km'}
                      </span>
                    )}
                  </div>
                  <div className="nbs-item-body">
                    <div className="nbs-item-top">
                      <span className="nbs-item-type">{p.propertyType}</span>
                      <span className="nbs-item-price">₹{p.price.toLocaleString()}<small>/mo</small></span>
                    </div>
                    <div className="nbs-item-title">{p.title}</div>
                    <div className="nbs-item-loc">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                      </svg>
                      {p.location?.area}, {p.location?.city}
                    </div>
                    {p.amenities?.length > 0 && (
                      <div className="nbs-item-tags">
                        {p.amenities.slice(0, 3).map(a => <span key={a}>{a}</span>)}
                      </div>
                    )}
                  </div>
                  <button
                    className={`nbs-heart ${isFavorite(p._id) ? 'active' : ''}`}
                    onClick={e => { e.stopPropagation(); toggleFavorite(p._id); }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24"
                      fill={isFavorite(p._id) ? '#ef4444' : 'none'}
                      stroke={isFavorite(p._id) ? '#ef4444' : '#94A3B8'} strokeWidth="2">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FinderNudge({ onDismiss, onFeedback }) {
  const [nudgeRating, setNudgeRating] = useState(0);
  return (
    <div className="fb-nudge" role="dialog" aria-label="Rate your search">
      <span className="fb-nudge-title"><span className="fbn-long">Finding what you need?</span><span className="fbn-short">Rate your search</span></span>
      <div className="fb-nudge-stars">
        {[1,2,3,4,5].map(s => (
          <button key={s} type="button" aria-label={`${s} star${s > 1 ? 's' : ''}`}
            className={`fb-nudge-star ${s <= nudgeRating ? 'lit' : ''}`}
            onClick={() => { setNudgeRating(s); setTimeout(onFeedback, 250); }}>
            <svg width="20" height="20" viewBox="0 0 24 24"
              fill={s <= nudgeRating ? '#f59e0b' : 'none'}
              stroke={s <= nudgeRating ? '#f59e0b' : '#94A3B8'} strokeWidth="1.6">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </button>
        ))}
      </div>
      <button className="fb-nudge-close" onClick={onDismiss} aria-label="Close">✕</button>
    </div>
  );
}

function PropertyCard({ property: p, eager, isFav, onToggleFav, onView }) {
  const { setSelectedProperty } = useProperty();
  const [imgFailed, setImgFailed] = useState(false);
  const img = !imgFailed && p.media?.images?.[0];
  return (
    <div className={`prop-card${p.isDemo ? ' prop-card-demo' : ''}`}>
      <div className="prop-card-img" onClick={() => { setSelectedProperty(p); onView?.(); }}>
        {img
          ? <img src={mediaUrl(img)} alt={p.title} loading={eager ? 'eager' : 'lazy'} fetchpriority={eager ? 'high' : undefined} decoding="async" onError={() => setImgFailed(true)} />
          : <div className="prop-card-placeholder">🏠</div>
        }
        <span className="prop-card-type">{p.propertyType}</span>
        {p.isDemo && <span className="prop-card-sample-badge">Sample</span>}
        <button className={`prop-heart-btn ${isFav ? 'active' : ''}`} aria-label={isFav ? 'Remove from saved' : 'Save'} onClick={e => { e.stopPropagation(); onToggleFav(); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill={isFav ? '#ef4444' : 'none'} stroke={isFav ? '#ef4444' : '#334155'} strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>
      </div>
      <div className="prop-card-body">
        <div className="prop-card-price">₹{p.price.toLocaleString()}<span>/{p.priceType === 'monthly' ? 'mo' : p.priceType}</span></div>
        <div className="prop-card-title">{p.title}</div>
        <div className="prop-card-location">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
          </svg>
          {p.location?.area}, {p.location?.city}
        </div>
        {p.amenities?.length > 0 && (
          <div className="prop-card-tags">
            {p.amenities.slice(0, 4).map(a => <span key={a} className="tag">{a}</span>)}
          </div>
        )}
        <div className="prop-card-footer">
          <span className="prop-card-views">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
            {p.views || 0} views
          </span>
          <button className="prop-card-cta" onClick={() => { setSelectedProperty(p); onView?.(); }}>View Details →</button>
        </div>
      </div>
    </div>
  );
}
